import React from 'react';
import { ChevronDown } from 'lucide-react';
import { tone } from './tones';

/**
 * Card whose body expands / collapses from its header (detail pages: client, store, store check,
 * contract, campaign). Collapsed by default.
 *  · `badges` render next to the title; `count` (+ `countTone`) is a shorthand quiet pill.
 *  · `extra` renders on the right of the header — clicks there don't toggle.
 *  · `flush` removes body padding (tables, lists that pad themselves).
 */
export default function CollapsibleCard({
    title,
    description,
    icon,
    iconTone = 'neutral',
    badges,
    count,
    countTone = 'neutral',
    extra,
    defaultExpanded = false,
    flush = false,
    children,
    className = ''
}) {
    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
    const toggle = () => setIsExpanded((v) => !v);

    return (
        <section className={`shelf-card ${className}`}>
            <div
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                    }
                }}
                className={`flex items-center justify-between gap-3 px-5 py-4 cursor-pointer select-none hover:bg-surface-25 transition-colors ${
                    isExpanded ? 'rounded-t-xl' : 'rounded-xl'
                }`}>
                <div className="flex items-center gap-3 min-w-0">
                    {icon && <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[15px] ${tone(iconTone).chip}`}>{icon}</span>}
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="m-0 text-[15px] font-semibold text-surface-900">{title}</h2>
                            {count !== undefined && count !== null && (
                                <span className={`inline-flex h-6 items-center rounded-md px-2 text-xs font-medium tabular-nums ${tone(countTone).chip}`}>
                                    {count}
                                </span>
                            )}
                            {badges}
                        </div>
                        {description && <p className="m-0 mt-0.5 text-xs text-surface-500">{description}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {extra && (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()} role="presentation">
                            {extra}
                        </div>
                    )}
                    <ChevronDown className={`text-[18px] text-surface-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
            </div>
            {isExpanded && <div className={`border-t border-surface-100 ${flush ? '[&_.ant-pagination]:px-5' : 'p-5'}`}>{children}</div>}
        </section>
    );
}
